// src/pages/Readymycard.js
import React, { useMemo, useState } from "react";
import cardData from "../components/card-data";
import Seo from "../components/seo";
import { currentLanguage, withLanguage } from "../utils/locale";
import { translateCardAlt, translateCardName, translateSuitName } from "../utils/i18n";

/* ================================
   API endpoint (no Worker changes needed)
   - Same origin: leave API_BASE empty.
   - Another domain: set REACT_APP_API_BASE / NEXT_PUBLIC_API_BASE / VITE_API_BASE.
================================ */
const API_BASE_RAW =
  // CRA / Next
  (typeof process !== "undefined" &&
    (process.env.REACT_APP_API_BASE || process.env.NEXT_PUBLIC_API_BASE)) ||
  // Vite
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE) ||
  // Same-origin fallback
  "";
const API_BASE = "https://tarotfate-api.tarotfate-com.workers.dev";

const TAROT_URL = `${API_BASE}/api/tarot/read`;

const siteUrl = "https://tarotfate.com";
const pageUrl = `${siteUrl}/readmycard`;
const ogImage = `${siteUrl}/icons/og-cover.jpg`;
const metaDescription =
  "Choose the tarot cards you pulled, mark upright or reversed and receive a reflective reading based on your exact selection.";

const schemaGraph = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${siteUrl}#org`,
      name: "TarotFate",
      url: siteUrl,
      logo: {
        "@type": "ImageObject",
        url: `${siteUrl}/icons/logo.webp`,
        width: 512,
        height: 512,
      },
    },
    {
      "@type": "WebSite",
      "@id": `${siteUrl}#website`,
      url: siteUrl,
      name: "TarotFate",
      publisher: { "@id": `${siteUrl}#org` },
      inLanguage: "en",
    },
    {
      "@type": "WebPage",
      "@id": `${pageUrl}#webpage`,
      url: pageUrl,
      name: "Read My Card | Choose Tarot Cards and Get a Reading | TarotFate",
      description: metaDescription,
      isPartOf: { "@id": `${siteUrl}#website` },
      primaryImageOfPage: { "@id": `${pageUrl}#image` },
      breadcrumb: { "@id": `${pageUrl}#breadcrumbs` },
      inLanguage: "en",
    },
    {
      "@type": "ImageObject",
      "@id": `${pageUrl}#image`,
      url: ogImage,
      width: 1200,
      height: 630,
      caption: "TarotFate cover",
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${pageUrl}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
        { "@type": "ListItem", position: 2, name: "Read My Card", item: pageUrl },
      ],
    },
    {
      "@type": "WebApplication",
      "@id": `${pageUrl}#tool`,
      name: "Read My Card",
      url: pageUrl,
      applicationCategory: "LifestyleApplication",
      operatingSystem: "Any",
      isAccessibleForFree: true,
      description: metaDescription,
    },
  ],
};

function schemaGraphForReadMyCard(isSpanish) {
  if (!isSpanish) return schemaGraph;
  const localizedPageUrl = `${siteUrl}/es/readmycard`;
  const localizedDescription =
    "Elige las cartas de tarot que sacaste, marca si estan al derecho o invertidas y recibe una lectura reflexiva segun tu seleccion exacta.";

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}#org`,
        name: "TarotFate",
        url: siteUrl,
        logo: {
          "@type": "ImageObject",
          url: `${siteUrl}/icons/logo.webp`,
          width: 512,
          height: 512,
        },
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}#website`,
        url: siteUrl,
        name: "TarotFate",
        publisher: { "@id": `${siteUrl}#org` },
        inLanguage: "es",
      },
      {
        "@type": "WebPage",
        "@id": `${localizedPageUrl}#webpage`,
        url: localizedPageUrl,
        name: "Leer mis cartas | Elige tus cartas y recibe una lectura | TarotFate",
        description: localizedDescription,
        isPartOf: { "@id": `${siteUrl}#website` },
        primaryImageOfPage: { "@id": `${localizedPageUrl}#image` },
        breadcrumb: { "@id": `${localizedPageUrl}#breadcrumbs` },
        inLanguage: "es",
      },
      {
        "@type": "ImageObject",
        "@id": `${localizedPageUrl}#image`,
        url: ogImage,
        width: 1200,
        height: 630,
        caption: "TarotFate",
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${localizedPageUrl}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Inicio", item: `${siteUrl}/es` },
          { "@type": "ListItem", position: 2, name: "Leer mis cartas", item: localizedPageUrl },
        ],
      },
      {
        "@type": "WebApplication",
        "@id": `${localizedPageUrl}#tool`,
        name: "Leer mis cartas",
        url: localizedPageUrl,
        applicationCategory: "LifestyleApplication",
        operatingSystem: "Any",
        isAccessibleForFree: true,
        description: localizedDescription,
      },
    ],
  };
}

/* ---------- Suit normalization ---------- */
function normalizeSuit(raw, name) {
  const v = String(raw || "").trim().toLowerCase();
  const n = String(name || "").toLowerCase();

  if (v === "major" || v === "major arcana") return "Major";
  if (v === "wands") return "Wands";
  if (v === "cups") return "Cups";
  if (v === "swords") return "Swords";
  if (v === "pentacles" || v === "coins") return "Pentacles";

  if (/wands?/.test(n)) return "Wands";
  if (/cups?/.test(n)) return "Cups";
  if (/swords?/.test(n)) return "Swords";
  if (/pentacles?/.test(n) || /coins?/.test(n)) return "Pentacles";
  return "Major";
}

/* ---------- Markdown → HTML (tiny) ---------- */
const mdToHtml = (md = "") =>
  md
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(/\n\n/g, "<br/><br/>")
    .replace(/\n/g, "<br/>");

/* ---------- Spread helpers (1 / 3 / 5) ---------- */
function chooseSpread(n) {
  if (n <= 1) return 1;
  if (n <= 3) return 3;
  return 5;
}

function buildWhisper({ tray, question }) {
  const list = tray
    .map((x, i) => `${i + 1}) ${x.name} (${x.orientation})`)
    .join("; ");

  const spread = chooseSpread(tray.length);
  const extra =
    tray.length > spread
      ? ` You selected ${tray.length} cards; please focus the reading on the first ${spread} in order and briefly weave the remaining cards into the closing guidance.`
      : "";

  return [
    question ? `Question: ${question}.` : "",
    `Use exactly these cards in this order: ${list}.`,
    extra,
  ]
    .filter(Boolean)
    .join(" ");
}

function looksEnglishReading(text = "") {
  return /\b(Your Reading|Card Drawn|This card|Upright|Reversed|of Cups|of Swords|of Wands|of Pentacles|the energy|green lights|brief reflection)\b/i.test(
    String(text)
  );
}

function fallbackSpanishReadMyCard(tray = []) {
  const cards = tray.length ? tray.slice(0, 5) : [{ name: "The Star", orientation: "upright" }];
  return cards
    .map((card) => {
      const orientation = card.orientation === "reversed" ? "invertida" : "al derecho";
      return `Carta extraida: **${translateCardName(card.name)} (${orientation})**\nEsta carta te invita a leer la situacion con calma y honestidad. Observa que energia esta disponible, que parte necesita cuidado y que paso pequeno puedes tomar sin forzarte. La lectura funciona mejor como espejo para decidir con mas claridad, no como una promesa fija.`;
    })
    .join("\n\n");
}

/* ================================
   Theme tokens (TarotCards vibe)
================================ */
const WRAP = "px-[1vw]";
const MAXW = "max-w-6xl mx-auto";
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";
const BORDER_SOFT = "border-white/15";
const PANEL_BASE =
  "relative overflow-hidden rounded-xl " +
  BORDER_SOFT +
  " shadow-[0_8px_28px_rgba(0,0,0,0.35)]";
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.32), rgba(3,121,155,0) 40%),
  linear-gradient(180deg, #014C75 0%, #005C83 22%, #016280ff 38%, #0C4B84 72%, #014C75 90%, #821E3C 100%)
`;
const SILK_TURQ =
  "bg-[linear-gradient(135deg,rgba(12,63,107,0.40),rgba(20,33,61,0.88))] " +
  "before:absolute before:inset-0 before:pointer-events-none before:bg-[radial-gradient(80%_60%_at_80%_0%,rgba(59,198,214,0.25),transparent_100%)] before:content-['']";

/* ================================
   Suit Accordion
================================ */
function SuitAccordion({ title, items, onAdd, recentlyAddedSet, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const isSpanish = currentLanguage() === "es";
  return (
    <div className={`${PANEL_BASE} ${SILK_TURQ} border`}>
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-white/95"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="text-base font-semibold uppercase tracking-[0.11em]">
          {translateSuitName(title)} <span className="opacity-70">({items.length})</span>
        </span>
        <span className="text-white/80 text-xl leading-none">{open ? "−" : "+"}</span>
      </button>

      {open && (
        <div className="px-4 pb-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {items.map((c) => {
              const wasJustAdded = recentlyAddedSet.has(c.slug);
              return (
                <div
                  key={c.slug}
                  className="rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 px-2 py-2"
                >
                  <button
                    onClick={() => onAdd(c.slug)}
                    className="group flex items-center gap-2 w-full text-left min-w-0"
                    title={isSpanish ? `Agregar ${translateCardName(c.name)}` : `Add ${c.name}`}
                  >
                    <div className="w-[44px] h-[66px] rounded bg-white/80 overflow-hidden shrink-0">
                      <img
                        src={c.image || `/card/${c.slug}.webp`}
                        alt={translateCardAlt(c.name)}
                        className="w-full h-full object-contain"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                      />
                    </div>
                    {/* Full name, clamp to 2 lines */}
                    <span
                      className="text-base leading-tight text-white/90 block min-w-0"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {translateCardName(c.name)}
                    </span>
                  </button>
                  <div
                    className={`mt-1 text-[11px] ${
                      wasJustAdded ? "text-emerald-200 opacity-100" : "text-white/60 opacity-0"
                    } transition-opacity duration-300`}
                  >
                    {wasJustAdded ? (isSpanish ? "Agregada" : "Added") : " "}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ================================
   Page
================================ */
export default function Readymycard() {
  const isSpanish = currentLanguage() === "es";
  const copy = isSpanish
    ? {
        seoTitle: "Leer mis cartas | Elige tus cartas y recibe una lectura | TarotFate",
        metaDescription:
          "Elige las cartas de tarot que sacaste, marca si estan al derecho o invertidas y recibe una lectura reflexiva segun tu seleccion exacta.",
        pageUrl: `${siteUrl}/es/readmycard`,
        intro:
          "Abre un palo, toca las cartas para agregarlas a tu bandeja. Apareceran abajo. Puedes invertirlas o quitarlas antes de pedir la lectura.",
        optionalQuestion: "Pregunta opcional",
        placeholder: "Sobre que te gustaria recibir claridad?",
        tray: "Tu bandeja",
        clear: "Limpiar",
        noCards: "Todavia no has elegido cartas.",
        flip: "Invertir",
        remove: "Quitar",
        readButton: "Leer mis cartas",
        emptyError: "Agrega al menos una carta a la bandeja.",
        readingTitle: "Tu lectura",
        emptyReading: 'Tu lectura aparecera aqui despues de tocar "Leer mis cartas".',
      }
    : {
        seoTitle: "Read My Card | Choose Tarot Cards and Get a Reading | TarotFate",
        metaDescription,
        pageUrl,
        intro:
          "Open a suit, tap cards to add them. They'll appear in your tray. Flip or remove as needed, then hit Read My Card.",
        optionalQuestion: "Optional question",
        placeholder: "What would you like insight on?",
        tray: "Your tray",
        clear: "Clear",
        noCards: "No cards selected yet.",
        flip: "Flip",
        remove: "Remove",
        readButton: "Read My Card",
        emptyError: "Please add at least one card to the tray.",
        readingTitle: "Your Reading",
        emptyReading: 'Your reading will appear here after you click "Read My Card".',
      };

  // Build suit buckets + quick index
  const { Major, Wands, Cups, Swords, Pentacles, allBySlug } = useMemo(() => {
    const all = Object.values(cardData || {});
    const by = { Major: [], Wands: [], Cups: [], Swords: [], Pentacles: [] };
    const index = {};
    all.forEach((c) => {
      if (!c) return;
      const suit = normalizeSuit(c.suit, c.name);
      if (!by[suit]) by[suit] = [];
      by[suit].push(c);
      if (c.slug) index[c.slug] = c;
    });
    Object.keys(by).forEach((k) =>
      by[k].sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
    );
    return { ...by, allBySlug: index };
  }, []);

  // UI state
  const [tray, setTray] = useState([]); // {id, slug, name, image, suit, orientation}
  const [question, setQuestion] = useState("");
  const [reading, setReading] = useState(null); // { reading: string }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Feedback when adding cards
  const [recentlyAdded, setRecentlyAdded] = useState(() => new Set());

  const addBySlug = (slug) => {
    const c = allBySlug[slug];
    if (!c) return;
    const item = {
      id: `${slug}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      slug: c.slug,
      name: c.name,
      image: c.image || `/card/${c.slug}.webp`,
      suit: normalizeSuit(c.suit, c.name),
      orientation: "upright",
    };
    setTray((t) => [...t, item]);

    // "Added ✓" feedback for 1.6s
    setRecentlyAdded((prev) => {
      const next = new Set(prev);
      next.add(slug);
      return next;
    });
    setTimeout(() => {
      setRecentlyAdded((prev) => {
        const next = new Set(prev);
        next.delete(slug);
        return next;
      });
    }, 1600);
  };

  const flip = (id) =>
    setTray((t) =>
      t.map((x) =>
        x.id === id ? { ...x, orientation: x.orientation === "upright" ? "reversed" : "upright" } : x
      )
    );

  const removeItem = (id) => setTray((t) => t.filter((x) => x.id !== id));
  const clearTray = () => setTray([]);

  // Submit to Worker /api/tarot (expects: { emotion, spread, whisper })
  const submit = async () => {
    if (tray.length === 0) {
      setError(copy.emptyError);
      setReading(null);
      return;
    }

    setError(null);
    setReading(null);
    setLoading(true);

    const spread = chooseSpread(tray.length);
   const payload = {
 insight: question || "",
 selection: tray.map(x => ({ name: x.name, orientation: x.orientation })),
 // (optional fallback; the Worker can parse this too)
 whisper: buildWhisper({ tray, question }),
};

    try {
      const res = await fetch(TAROT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(withLanguage(payload)),
      });
      if (!res.ok) {
        const txt = await res.text();
        console.error("[/api/tarot] error", res.status, txt);
        throw new Error(`API error ${res.status}: ${txt.slice(0, 200)}`);
      }
      const data = await res.json(); // { reading: string }
      setReading(
        isSpanish && looksEnglishReading(data?.reading)
          ? { ...data, reading: fallbackSpanishReadMyCard(tray), fallback: true }
          : data
      );
    } catch (e) {
      console.error("[/api/tarot] fetch failed", e);
      setReading({
        reading:
          isSpanish
            ? fallbackSpanishReadMyCard(tray)
            : "We couldn't reach the reading service right now. Here's a quick, on-device reflection:\n\n" +
              tray
                .slice(0, 5)
                .map(
                  (x, i) => `**Card ${i + 1}: ${x.name} (${x.orientation})** - brief reflection.`
                )
                .join("\n\n"),
        offline: true,
      });
      setError(e.message || "Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${FONT} min-h-screen relative overflow-hidden`} style={{ background: CANVAS_BG }}>
      <Seo
        title={copy.seoTitle}
        description={copy.metaDescription}
        url={copy.pageUrl}
        image={ogImage}
        schema={schemaGraphForReadMyCard(isSpanish)}
      />

      {/* Ambient auras */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[160px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
        <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[140px] bg-[radial-gradient(circle,rgba(140,59,90,0.55)_0%,transparent_70%)]" />
      </div>

      {/* Header */}
      <section className={`pt-10 ${WRAP} ${MAXW} relative z-[1]`}>
        <div className="flex justify-center">
          <img
src="/icons/readmycard.webp"
alt={isSpanish ? "Leer mis cartas" : "Read My Card"}
className="w-36 h-auto sm:w-38 md:w-44 lg:w-50 object-contain mx-auto select-none pointer-events-none"
onContextMenu={(e) => e.preventDefault()}
draggable="false"
/>
        </div>
        <div className="flex items-center justify-center my-2">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
          <span className="px-4 text-white/90">✦</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
        <p className={`${TEXT} text-center max-w-3xl mx-auto`}>
          {copy.intro}
        </p>
      </section>

      {/* Suit Accordions (all closed by default) */}
      <section className={`mt-8 space-y-3 ${WRAP} ${MAXW} relative z-[1]`}>
        <SuitAccordion title="Major Arcana" items={Major} onAdd={addBySlug} recentlyAddedSet={recentlyAdded} />
        <SuitAccordion title="Wands" items={Wands} onAdd={addBySlug} recentlyAddedSet={recentlyAdded} />
        <SuitAccordion title="Cups" items={Cups} onAdd={addBySlug} recentlyAddedSet={recentlyAdded} />
        <SuitAccordion title="Swords" items={Swords} onAdd={addBySlug} recentlyAddedSet={recentlyAdded} />
        <SuitAccordion title="Pentacles" items={Pentacles} onAdd={addBySlug} recentlyAddedSet={recentlyAdded} />
      </section>

      {/* Tray + Controls */}
      <section className={`mt-6 ${WRAP} ${MAXW} relative z-[1]`}>
        <div className={`${PANEL_BASE} ${SILK_TURQ} border p-4 sm:p-6`}>
          {/* Optional question AT THE TOP */}
          <div className="mb-4">
            <label className="text-white/80 text-xs mb-1 block">{copy.optionalQuestion}</label>
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={copy.placeholder}
              className="w-full rounded-xl border border-white/20 bg-white/10 text-white/95 placeholder-white/60 px-3 py-2 text-sm outline-none focus:border-white/40"
            />
          </div>

          {/* Header row */}
          <div className="flex items-center justify-between">
            <h3 className="text-white/95 text-base font-normal">{copy.tray} ({tray.length})</h3>
            <button
              onClick={clearTray}
              className="rounded-xl border border-white/25 bg-white/10 text-white/95 px-3 py-2 text-sm hover:bg-white/20 transition"
            >
              {copy.clear}
            </button>
          </div>

          {/* Tray grid - vertical: image, then name, then actions */}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {tray.length === 0 ? (
              <p className="text-white/75 text-sm">{copy.noCards}</p>
            ) : (
              tray.map((x) => (
                <div key={x.id} className="rounded-lg border border-white/15 bg-white/5 px-2 py-2">
                  <div className="flex flex-col items-center">
                    {/* Image */}
                    <div className="w-[100px] h-auto rounded bg-white/80 overflow-hidden">
                      <img
                        src={x.image}
                        alt={translateCardAlt(x.name)}
                        className={`w-full h-full object-contain ${
                          x.orientation === "reversed" ? "rotate-180" : ""
                        }`}
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                      />
                    </div>

                    {/* Name under image (text-base, 2-line clamp, centered) */}
                    <div
                      className="mt-2 text-base leading-tight text-white/90 text-center"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        maxWidth: "10rem",
                      }}
                      title={x.name}
                    >
                      {translateCardName(x.name)}
                    </div>
                  </div>

                  {/* Actions (colored, compact, wraps if tight) */}
                  <div className="mt-2 flex gap-2 flex-wrap justify-center">
                    <button
                      onClick={() => flip(x.id)}
                      className="
                        inline-flex items-center gap-1 rounded-md border
                        border-emerald-300/40 bg-emerald-500/20 text-emerald-100
                        px-2 py-[2px] text-[15px]
                        hover:bg-emerald-500/30 hover:border-emerald-300/60
                        focus:outline-none focus:ring-2 focus:ring-emerald-300/40
                        active:translate-y-[0.5px] transition
                      "
                      aria-label={copy.flip}
                    >
                      ↻ {copy.flip}
                    </button>
                    <button
                      onClick={() => removeItem(x.id)}
                      className="
                        inline-flex items-center gap-1 rounded-md border
                        border-rose-300/40 bg-rose-500/20 text-rose-100
                        px-2 py-[2px] text-[15px]
                        hover:bg-rose-500/30 hover:border-rose-300/60
                        focus:outline-none focus:ring-2 focus:ring-rose-300/40
                        active:translate-y-[0.5px] transition
                      "
                      aria-label={copy.remove}
                    >
                      ✕ {copy.remove}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* CTA */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={submit}
              disabled={tray.length === 0}
              className={`rounded-xl px-4 py-2 text-sm transition ${
                tray.length > 0
                  ? "bg-white text-slate-900 hover:opacity-90"
                  : "bg-white/30 text-white/70 cursor-not-allowed"
              }`}
            >
              {copy.readButton}
            </button>
          </div>

          <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </section>

      {/* Reading result (inline, below cards) */}
      <section className={`mt-4 mb-16 ${WRAP} ${MAXW} relative z-[1]`}>
        <div className={`${PANEL_BASE} bg-white text-slate-800 border border-white/70 p-4 sm:p-6`}>
          <h4 className="text-slate-700 font-semibold mb-2">{copy.readingTitle}</h4>

          {loading ? (
            <div className="py-6">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-slate-200 rounded" />
                <div className="h-4 bg-slate-200 rounded w-5/6" />
                <div className="h-4 bg-slate-200 rounded w-3/5" />
              </div>
            </div>
          ) : reading?.reading ? (
            <article
              className="prose prose-slate max-w-none"
              dangerouslySetInnerHTML={{ __html: mdToHtml(reading.reading) }}
            />
          ) : error ? (
            <p className="text-rose-600 text-sm">{String(error)}</p>
          ) : (
            <p className="text-slate-600 text-sm">
              {copy.emptyReading}
            </p>
          )}
        </div>
        {/* Divider */}
<div className="my-10 h-px w-full bg-gradient-to-r from-transparent via-white/30 to-transparent" />

<section className="max-w-4xl mx-auto">
  {isSpanish ? (
    <>
      <h2 className="text-xl font-semibold text-white">Como usar <em>Leer mis cartas</em> con tu propia baraja</h2>
      <ol className="list-decimal ml-5 mt-3 space-y-2 text-white/90">
        <li><strong>Saca una carta en casa.</strong> Mira el nombre de la carta y si salio al derecho o invertida.</li>
        <li><strong>Elige la carta exacta aqui.</strong> Abre el palo, selecciona la carta y ponla en la bandeja. Si salio invertida, marcala como invertida.</li>
        <li><strong>Agrega una pregunta si quieres.</strong> Escribe un enfoque breve, como amor esta semana, trabajo este mes o una decision concreta.</li>
        <li><strong>Toca <em>Leer mis cartas</em>.</strong> Recibiras una interpretacion clara y ajustada a tu seleccion.</li>
        <li><strong>Reflexiona y luego actua.</strong> Usa la lectura como espejo para decidir, no como prediccion fija.</li>
      </ol>

      <h3 className="mt-6 text-lg font-semibold text-white">Guia rapida de lectura</h3>
      <div className="grid sm:grid-cols-2 gap-4 mt-2 text-white/90">
        <div className="rounded-md border border-white/20 p-4">
          <p className="font-semibold">Al derecho e invertida</p>
          <ul className="list-disc ml-5 mt-1 space-y-1">
            <li><strong>Al derecho:</strong> la energia fluye con mas claridad y apoyo.</li>
            <li><strong>Invertida:</strong> la energia puede estar bloqueada, interiorizada o pidiendo ajuste.</li>
          </ul>
        </div>
        <div className="rounded-md border border-white/20 p-4">
          <p className="font-semibold">Arcanos Mayores y Menores</p>
          <ul className="list-disc ml-5 mt-1 space-y-1">
            <li><strong>Arcanos Mayores:</strong> temas profundos, cambios internos y lecciones importantes.</li>
            <li><strong>Arcanos Menores:</strong> situaciones cotidianas, decisiones practicas y pasos concretos.</li>
          </ul>
        </div>
        <div className="rounded-md border border-white/20 p-4">
          <p className="font-semibold">Palos del tarot</p>
          <ul className="list-disc ml-5 mt-1 space-y-1">
            <li><strong>Bastos:</strong> accion, iniciativa y creatividad.</li>
            <li><strong>Copas:</strong> emociones, intuicion y relaciones.</li>
            <li><strong>Espadas:</strong> pensamiento, verdad y comunicacion.</li>
            <li><strong>Oros:</strong> trabajo, dinero, cuerpo y estabilidad.</li>
          </ul>
        </div>
        <div className="rounded-md border border-white/20 p-4">
          <p className="font-semibold">Numeros de un vistazo</p>
          <p className="mt-1">As: inicio. Dos: eleccion. Tres: crecimiento. Cuatro: estabilidad. Cinco: desafio. Seis: armonia. Siete: prueba. Ocho: movimiento. Nueve: culminacion. Diez: cierre.</p>
          <p className="mt-1">Sota: aprendizaje. Caballero: impulso. Reina: maestria interior. Rey: liderazgo y estructura.</p>
        </div>
      </div>

      <h3 className="mt-6 text-lg font-semibold text-white">Proposito</h3>
      <p className="text-white/90"><em>Leer mis cartas</em> te ayuda a interpretar <strong>la carta exacta que sacaste</strong>. Es una herramienta reflexiva para aclarar tu siguiente paso, no una promesa de resultados.</p>

      <h3 className="mt-6 text-lg font-semibold text-white">Notas</h3>
      <ul className="list-disc ml-5 mt-2 space-y-1 text-white/90">
        <li><strong>Respeta tu tirada:</strong> elige la carta y orientacion correctas antes de leer.</li>
        <li><strong>Un enfoque a la vez:</strong> si tienes varias preguntas, haz lecturas separadas.</li>
        <li><strong>Etica:</strong> evita depender del tarot para decisiones medicas, legales o financieras.</li>
        <li><strong>Privacidad:</strong> tu pregunta solo se usa para dar contexto a la lectura. Puedes omitir detalles sensibles.</li>
      </ul>
    </>
  ) : (
    <>
      <h2 className="text-xl font-semibold text-white">How to Use <em>Read My Card</em> (with your own deck)</h2>
      <ol className="list-decimal ml-5 mt-3 space-y-2 text-white/90">
        <li><strong>Draw a card at home.</strong> Look at your physical card's <em>name</em> and orientation.</li>
        <li><strong>Select the exact card here.</strong> Pick the suit, then the card and place it in the tray. Mark it reversed if needed.</li>
        <li><strong>Add your insight if helpful.</strong> Keep it short and specific.</li>
        <li><strong>Press <em>Read My Card</em>.</strong> You will get a clear interpretation tuned to your selection.</li>
        <li><strong>Reflect, then act.</strong> Treat the reading as a mirror for decision-making, not a prediction.</li>
      </ol>
    </>
  )}
</section>

      </section>
      
    </div>
  );
}
